<template>
	<div class="chat-layout" :class="colourMode">
		<!-- Main chat area -->
		<div class="chat-page">
			<!-- Settings View -->
			<SettingsOverlay
				v-if="showSettings"
				:customer="customer"
				:colourMode="colourMode"
				:safeTop="safeTop"
				@close="showSettings = false"
				@update-pref="updatePref"
				@select-colour-mode="selectColourMode"
			/>

			<!-- Welcome screen shown when no messages sent yet -->
			<div v-else-if="!hasStartedChat" class="welcome-screen" :class="{ 'dropdown-open': openCategory }">
				<div class="welcome-content">
					<div class="welcome-icon">
						<img :src="require('@/assets/mg.png')" alt="MG" />
					</div>
					<h1 class="welcome-greeting">{{ greeting }}, {{ userName }}</h1>
				</div>
				<!-- Large centered input on welcome screen -->
				<div class="welcome-input-wrapper">
					<div class="welcome-input-container">
						<textarea
							ref="welcomeInput"
							v-model="message"
							class="welcome-input"
							placeholder="How can I help you today?"
							@keydown.enter.exact.prevent="sendMessage"
							rows="1"
						></textarea>
						<div class="welcome-input-footer">
							<button class="attach-button" @click="attach">
								<v-icon>mdi-paperclip</v-icon>
							</button>
							<button
								class="send-button"
								:class="{ active: message && message.trim() }"
								:disabled="!message || !message.trim()"
								@click="sendMessage"
							>
								<v-icon>mdi-arrow-up</v-icon>
							</button>
						</div>
					</div>

					<!-- Suggestion chips -->
					<div class="suggestion-chips">
						<button
							v-for="cat in suggestionCategories"
							:key="cat.id"
							class="suggestion-chip"
							:class="{ active: openCategory === cat.id }"
							@click="toggleCategory(cat.id)"
						>
							<v-icon size="16">{{ cat.icon }}</v-icon>
							<span>{{ cat.label }}</span>
						</button>
					</div>

					<!-- Suggestion dropdown -->
					<transition name="dropdown">
						<div v-if="openCategory" class="suggestion-dropdown">
							<div class="dropdown-header">
								<v-icon size="18">{{ activeCategory?.icon }}</v-icon>
								<span>{{ activeCategory?.label }}</span>
								<button class="dropdown-close" @click="openCategory = null">
									<v-icon size="18">mdi-close</v-icon>
								</button>
							</div>
							<div
								v-for="(suggestion, index) in activeCategory?.suggestions"
								:key="index"
								class="dropdown-item"
								@click="selectSuggestion(suggestion)"
							>
								{{ suggestion }}
							</div>
						</div>
					</transition>
				</div>
			</div>

			<!-- Messages shown after chat starts -->
			<div v-else class="messagesCanvas" ref="messagesCanvas">
				<div class="messages-container">
					<div v-for="(m, index) in messages" :key="index" :class="['message-row', m.direction]">
						<div v-if="m.direction === 'in'" class="assistant-icon">
							<img :src="require('@/assets/mg.png')" alt="MG" />
						</div>
						<div :class="['message', m.direction]" v-html="formatMessage(applyMonetaryMask(m.body))"></div>
					</div>
					<div v-if="typing" class="message-row in">
						<div class="assistant-icon">
							<img :src="require('@/assets/mg.png')" alt="MG" />
						</div>
						<div class="typing-indicator">
							<span class="dot"></span>
							<span class="dot"></span>
							<span class="dot"></span>
						</div>
					</div>
				</div>
				<div ref="end"></div>
			</div>

			<!-- Bottom input bar (only shown after chat starts, not in settings) -->
			<div v-if="hasStartedChat && !showSettings" class="input-wrapper">
				<div class="input-container">
					<input
						ref="chatInput"
						v-model="message"
						type="text"
						class="chat-input"
						placeholder="Reply to MG..."
						@keydown.enter.exact.prevent="sendMessage"
					/>
					<button class="attach-button" @click="attach">
						<v-icon>mdi-paperclip</v-icon>
					</button>
					<button
						class="send-button"
						:class="{ active: message && message.trim() }"
						:disabled="!message || !message.trim()"
						@click="sendMessage"
					>
						<v-icon>mdi-arrow-up</v-icon>
					</button>
				</div>
			</div>
		</div>
	</div>

	<WhatsAppEnableDrawer
		ref="whatsAppEnableDrawer"
		@enable="handleEnableWhatsApp"
		@done="handleWhatsAppEnableDone"
		@closed="handleWhatsAppDrawerClosed"
	/>
</template>

<script>
import Services from '../../mixins/Services';
import WhatsAppMixin from '../../mixins/WhatsAppMixin';
import SettingsOverlay from '../components/SettingsOverlay.vue';
import WhatsAppEnableDrawer from '../../components/elements/WhatsAppEnableDrawer.vue';

export default {
	name: "ChatPage",
	mixins: [Services, WhatsAppMixin],
	components: { SettingsOverlay, WhatsAppEnableDrawer },
	emits: ['colour-mode-changed'],
	inject: ['registerSidebar', 'updateSidebarPageItems'],
	data() {
		return {
			message: '',
			messages: [],
			typing: false,
			hasStartedChat: false,
			userName: 'there',
			openCategory: null,
			sidebarOpen: false,
			currentChatId: null,
			recentChats: [],
			colourMode: 'dark',
			showSettings: false,
			suggestionCategories: [
				{
					id: 'spending',
					label: 'Spending',
					icon: 'mdi-chart-pie',
					suggestions: [
						'Show me my spending this month',
						'Where am I spending the most?',
						'Help me create a budget',
						'What are my recurring payments?'
					]
				},
				{
					id: 'savings',
					label: 'Savings',
					icon: 'mdi-piggy-bank',
					suggestions: [
						'How can I save more money?',
						'Help me set a savings goal',
						'What should my emergency fund be?',
						'Tips for saving on groceries'
					]
				},
				{
					id: 'accounts',
					label: 'Accounts',
					icon: 'mdi-bank',
					suggestions: [
						'Show me my account balances',
						'Which accounts are linked?',
						'Help me link a new account',
						'What is my net worth?'
					]
				},
				{
					id: 'benefits',
					label: 'Benefits',
					icon: 'mdi-gift',
					suggestions: [
						'What employee benefits do I have?',
						'How do I use my benefits?',
						'Am I missing any benefits?',
						'Explain salary sacrifice'
					]
				},
				{
					id: 'vouchers',
					label: 'Vouchers',
					icon: 'mdi-ticket-percent',
					suggestions: [
						'Find me the best voucher deals',
						'How do cashback vouchers work?',
						'What vouchers do I have?',
						'Recommend vouchers for groceries'
					]
				}
			]
		}
	},
	computed: {
		safeTop() {
			return parseInt(this.$cookies.get('st') ?? 0);
		},
		greeting() {
			const hour = new Date().getHours();
			if (hour < 12) return 'Good morning';
			if (hour < 17) return 'Good afternoon';
			return 'Good evening';
		},
		activeCategory() {
			return this.suggestionCategories.find(c => c.id === this.openCategory);
		}
	},
	methods: {
		loadChatsFromStorage() {
			try {
				const stored = localStorage.getItem('mg_chat_history');
				if (stored) {
					this.recentChats = JSON.parse(stored);
				}
			} catch {
				this.recentChats = [];
			}
		},
		saveChatsToStorage() {
			localStorage.setItem('mg_chat_history', JSON.stringify(this.recentChats));
		},
		saveCurrentChat() {
			if (!this.currentChatId || this.messages.length === 0) return;

			const chatIndex = this.recentChats.findIndex(c => c.id === this.currentChatId);
			if (chatIndex !== -1) {
				this.recentChats[chatIndex].messages = [...this.messages];
				this.saveChatsToStorage();
			}
		},
		startNewChat() {
			// Save current chat before starting new one
			this.saveCurrentChat();

			this.messages = [];
			this.hasStartedChat = false;
			this.currentChatId = null;
			this.sidebarOpen = false;
			this.$nextTick(() => {
				this.$refs.welcomeInput?.focus();
			});
		},
		createNewChatEntry(firstMessage) {
			const title = firstMessage.length > 35
				? firstMessage.substring(0, 35) + '...'
				: firstMessage;
			const newChat = {
				id: Date.now(),
				title: title,
				messages: []
			};
			this.recentChats.unshift(newChat);
			this.currentChatId = newChat.id;
			this.saveChatsToStorage();
		},
		loadChat(chatId) {
			// Save current chat before loading another
			this.saveCurrentChat();

			const chat = this.recentChats.find(c => c.id === chatId);
			if (chat) {
				this.currentChatId = chatId;
				this.messages = chat.messages ? [...chat.messages] : [];
				this.hasStartedChat = this.messages.length > 0;
			}
			this.sidebarOpen = false;
		},
		deleteChat(chatId) {
			const index = this.recentChats.findIndex(c => c.id === chatId);
			if (index !== -1) {
				this.recentChats.splice(index, 1);
				this.saveChatsToStorage();

				// If deleting the current chat, reset to new chat state
				if (chatId === this.currentChatId) {
					this.messages = [];
					this.hasStartedChat = false;
					this.currentChatId = null;
				}
			}
		},
		renameChat({ id, title }) {
			const chat = this.recentChats.find(c => c.id === id);
			if (chat) {
				chat.title = title;
				this.saveChatsToStorage();
			}
		},
		toggleCategory(categoryId) {
			this.openCategory = this.openCategory === categoryId ? null : categoryId;
		},
		selectSuggestion(suggestion) {
			this.message = suggestion;
			this.openCategory = null;
			this.sendMessage();
		},
		scrollToEnd() {
			this.$nextTick(() => {
				if (this.$refs["end"]) {
					this.$refs["end"].scrollIntoView({ behavior: 'smooth' });
				}
			});
		},
		sendMessage() {
			if (!this.message || this.message.trim() === '' || this.typing) return;

			const userMessage = this.message.trim();
			const isFirstMessage = !this.hasStartedChat;

			this.hasStartedChat = true;
			this.messages.push({ direction: "out", body: userMessage });
			this.message = "";
			this.typing = true;
			this.scrollToEnd();

			// Create new chat entry on first message
			if (isFirstMessage) {
				this.createNewChatEntry(userMessage);
			}

			// Handle special commands
			if (userMessage.toLowerCase() === "litmustest") {
				this.$router.push('/litmustestpage');
				return;
			}
			if (userMessage.toLowerCase() === "admin") {
				this.$router.push('/admin');
				return;
			}

			this._sendChatMessage(userMessage, (res) => {
				if (!res.keepThinking) {
					this.typing = false;
				}
				this.messages.push({ direction: "in", body: res.body });
				this.scrollToEnd();
				this.saveCurrentChat();
			});
		},
		formatMessage(text) {
			if (!text) return '';
			let html = text
				.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
				.replace(/\*([^*]+)\*/g, '<em>$1</em>');
			html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
			html = html.replace(/(<br>)+\s*(\d+\.) /g, '<br>$2 ');
			html = html.replace(/\n\s*- /g, '<br>&bull; ');
			html = html.replace(/\n/g, '<br>');
			return html;
		},
		applyMonetaryMask(text) {
			if (localStorage.getItem('randomiseFigures') === 'true' && this._processMonetaryAmounts) {
				return this._processMonetaryAmounts(text);
			}
			return text;
		},
		attach() {
			// Reload customer from server to get current whatsAppEnabled state
			this._clearCustomer();
			this.customer = null;
			this._getCustomer(() => {
				if (this.customer.person.whatsAppEnabled) {
					this.hasStartedChat = true;
					this.messages.push({direction: "in", body: "Check your WhatsApp!"});
					this.scrollToEnd();
					this._sendWhatsAppNotification("You indicated you wanted to send a document 📄  Just drop it here and we will upload and process it for you 👍");
				} else {
					if (this.$refs.whatsAppEnableDrawer) {
						this.$refs.whatsAppEnableDrawer.openDrawer();
					}
				}
			});
		},
		onLoad() {
			this._getCustomer(() => {
				if (this.customer?.person?.firstName) {
					this.userName = this.customer.person.firstName;
				}
				// Initialize preferences if null
				if (this.customer && !this.customer.preferences) {
					this.customer.preferences = {
						dailyCurrentBalanceNotifications: false,
						dailyCreditBalanceNotifications: false,
						quizNotifications: false
					};
				}
			});
		},
		updatePref(key, value) {
			if (this.customer?.preferences) {
				this.customer.preferences[key] = value;
				this._savePreferences();
			}
		},
		handleLogout() {
			this.$cookies.remove('token');
			this.$router.push({ path: '/login', query: { landingpage: this.$route.path } });
		},
		selectColourMode(mode) {
			this.colourMode = mode;
			localStorage.setItem('mg_colour_mode', mode);
			this.$emit('colour-mode-changed', mode);
		}
	},
	mounted() {
		this.loadChatsFromStorage();
		this.onLoad();
		const savedMode = localStorage.getItem('mg_colour_mode');
		if (savedMode) {
			this.colourMode = savedMode;
		} else {
			this.colourMode = 'light';
			localStorage.setItem('mg_colour_mode', 'light');
		}
		this.$nextTick(() => {
			this.$refs.welcomeInput?.focus();
		});
		// Register sidebar config with parent Desktop layout
		if (this.registerSidebar) {
			this.registerSidebar({
				config: {
					actionButton: { icon: 'mdi-plus', label: 'New chat' },
					pageItemsTitle: 'Recents',
					pageItemsEmptyText: 'No recent chats'
				},
				data: this,
				handlers: {
					onActionClick: () => this.startNewChat(),
					onPageItemClick: (id) => this.loadChat(id),
					onPageItemDelete: (id) => this.deleteChat(id),
					onPageItemRename: (data) => this.renameChat(data),
					onOpenSettings: () => { this.showSettings = true; }
				}
			});
			// Sync loaded chats to sidebar immediately
			if (this.updateSidebarPageItems) {
				this.updateSidebarPageItems(this.recentChats);
			}
		}
	},
	watch: {
		hasStartedChat(val) {
			if (val) {
				this.$nextTick(() => {
					this.$refs.chatInput?.focus();
				});
			}
		},
		recentChats: {
			handler(newVal) {
				if (this.updateSidebarPageItems) {
					this.updateSidebarPageItems(newVal);
				}
			},
			deep: true
		}
	}
}
</script>

<style scoped>

/* Local layout variables */
.chat-layout {
	--sidebar-width: 260px;
	--max-content-width: 720px;
}

/* Layout */
.chat-layout {
	display: flex;
	background-color: var(--dark-bg-primary);
	height: 100%;
}

.chat-page {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
	background-color: var(--dark-bg-primary);
}

/* Welcome Screen */
.welcome-screen {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	padding: 20vh 20px 20px;
	overflow-y: hidden;
	overscroll-behavior: contain;
}

.welcome-screen.dropdown-open {
	padding-bottom: 300px;
	overflow-y: auto;
}

.welcome-content {
	text-align: center;
	margin-bottom: 32px;
	padding: 0 20px;
}

.welcome-icon {
	margin-bottom: 24px;
}

.welcome-icon img {
	width: 48px;
	height: 48px;
	border-radius: 50%;
}

.welcome-greeting {
	color: var(--dark-text-primary);
	font-size: 28px;
	margin: 0;
	letter-spacing: -0.5px;
	word-wrap: break-word;
}

/* Welcome input */
.welcome-input-wrapper {
	width: 100%;
	max-width: var(--max-content-width);
	padding: 0 20px;
	position: relative;
}

.welcome-input-container {
	background: var(--dark-bg-secondary);
	border-radius: 24px;
	border: 1px solid var(--dark-border);
	padding: 16px 20px 12px;
}

.welcome-input {
	width: 100%;
	background: transparent;
	border: none;
	outline: none;
	color: var(--dark-text-primary);
	font-size: 16px;
	resize: none;
	min-height: 24px;
	max-height: 120px;
	line-height: 1.5;
}

.welcome-input::placeholder {
	color: var(--dark-text-secondary);
}

.welcome-input-footer {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 8px;
	margin-top: 12px;
}

/* Suggestion chips */
.suggestion-chips {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 8px;
	margin-top: 16px;
}

.suggestion-chip {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 16px;
	background: transparent;
	border: 1px solid var(--dark-border);
	border-radius: 20px;
	color: var(--dark-text-primary);
	font-size: 14px;
	cursor: pointer;
	transition: background-color 0.2s, border-color 0.2s;
}

.suggestion-chip:hover {
	background: var(--dark-bg-secondary);
	border-color: #666;
}

.suggestion-chip.active {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-text-secondary);
}

.suggestion-chip .v-icon {
	color: var(--dark-text-secondary);
}

/* Suggestion dropdown */
.suggestion-dropdown {
	position: absolute;
	top: 100%;
	left: 20px;
	right: 20px;
	margin-top: 12px;
	background: var(--dark-bg-secondary);
	border: 1px solid var(--dark-border);
	border-radius: 16px;
	overflow: hidden;
	z-index: 50;
}

.dropdown-header {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	color: var(--dark-text-secondary);
	font-size: 14px;
	border-bottom: 1px solid var(--dark-border);
}

.dropdown-header .v-icon {
	color: var(--dark-text-secondary);
}

.dropdown-close {
	margin-left: auto;
	background: transparent;
	border: none;
	color: var(--dark-text-secondary);
	cursor: pointer;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.dropdown-close:hover {
	color: var(--dark-text-primary);
}

.dropdown-item {
	padding: 14px 16px;
	color: var(--dark-text-primary);
	font-size: 15px;
	cursor: pointer;
	border-bottom: 1px solid var(--dark-bg-hover);
	transition: background-color 0.15s;
}

.dropdown-item:last-child {
	border-bottom: none;
}

.dropdown-item:hover {
	background: var(--dark-bg-hover);
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
	transition: opacity 0.2s, transform 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
	opacity: 0;
	transform: translateY(-8px);
}

/* Messages */
.messagesCanvas {
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
	overscroll-behavior: contain;
	padding: 80px 12px 120px;
}

.messages-container {
	max-width: var(--max-content-width);
	margin: 0 auto;
}

.message-row {
	display: flex;
	align-items: flex-start;
	margin-bottom: 24px;
	gap: 10px;
}

.message-row.out {
	justify-content: flex-end;
}

.message-row.in {
	justify-content: flex-start;
}

.assistant-icon {
	flex-shrink: 0;
	width: 28px;
	height: 28px;
}

.assistant-icon img {
	width: 100%;
	height: 100%;
	border-radius: 50%;
}

.message {
	max-width: 85%;
	padding: 12px 16px;
	border-radius: 18px;
	font-size: 15px;
	line-height: 1.5;
	word-break: break-word;
	color: var(--dark-text-primary);
}

.message.in {
	background: transparent;
	padding-left: 0;
}

.message.out {
	background: var(--dark-bg-secondary);
}

.message :deep(a) {
	color: #c4a77d;
	text-decoration: underline;
}

.message :deep(strong) {
	color: #fff;
}

/* Typing Indicator */
.typing-indicator {
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 12px 0;
}

.dot {
	width: 8px;
	height: 8px;
	background: var(--dark-text-secondary);
	border-radius: 50%;
	animation: typing-pulse 1.4s infinite;
}

.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes typing-pulse {
	0%, 60%, 100% {
		opacity: 0.4;
		transform: scale(0.8);
	}
	30% {
		opacity: 1;
		transform: scale(1);
	}
}

/* Bottom Input */
.input-wrapper {
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	padding: 16px 20px 32px;
	background: linear-gradient(transparent, var(--dark-bg-primary) 20%);
}

.input-container {
	max-width: var(--max-content-width);
	margin: 0 auto;
	display: flex;
	align-items: center;
	gap: 4px;
	background: var(--dark-bg-secondary);
	border-radius: 24px;
	padding: 8px 8px 8px 20px;
	border: 1px solid var(--dark-border);
}

.chat-input {
	flex: 1;
	background: transparent;
	border: none;
	outline: none;
	color: var(--dark-text-primary);
	font-size: 16px;
	padding: 8px 0;
}

.chat-input::placeholder {
	color: var(--dark-text-secondary);
}

/* Send Button */
.send-button {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	border: none;
	background: var(--dark-border);
	color: var(--dark-text-secondary);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: background-color 0.2s, color 0.2s;
}

.send-button.active {
	background: var(--dark-accent);
	color: #fff;
}

.send-button:disabled {
	cursor: default;
}

.send-button .v-icon {
	font-size: 20px;
}

/* Attach Button */
.attach-button {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	border: none;
	background: transparent;
	color: var(--dark-text-secondary);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: color 0.2s;
}

.attach-button:hover {
	color: var(--dark-text-primary);
}

.attach-button .v-icon {
	font-size: 20px;
}

/* Light Mode */
.chat-layout.light {
	background-color: var(--background-color-main);
}

.chat-layout.light .chat-page {
	background-color: var(--background-color-main);
}

.chat-layout.light .welcome-greeting {
	color: var(--text-color);
}

.chat-layout.light .welcome-input-container {
	background: var(--background-color);
	border-color: var(--border-color);
}

.chat-layout.light .welcome-input {
	color: var(--text-color);
}

.chat-layout.light .welcome-input::placeholder {
	color: var(--grey-text);
}

.chat-layout.light .suggestion-chip {
	border-color: var(--border-color);
	color: var(--text-color);
}

.chat-layout.light .suggestion-chip:hover {
	background: var(--background-color-alt);
	border-color: var(--border-color);
}

.chat-layout.light .suggestion-chip.active {
	background: var(--background-color-alt);
	border-color: var(--grey-text);
}

.chat-layout.light .suggestion-chip .v-icon {
	color: var(--grey-text);
}

.chat-layout.light .suggestion-dropdown {
	background: var(--background-color);
	border-color: var(--border-color);
}

.chat-layout.light .dropdown-header {
	color: var(--grey-text);
	border-color: var(--background-color-alt);
}

.chat-layout.light .dropdown-close {
	color: var(--grey-text);
}

.chat-layout.light .dropdown-item {
	color: var(--text-color);
	border-color: var(--background-color-alt);
}

.chat-layout.light .dropdown-item:hover {
	background: var(--background-color-alt);
}

.chat-layout.light .message {
	color: var(--text-color);
}

.chat-layout.light .message.out {
	background: var(--background-color-alt);
}

.chat-layout.light .message :deep(strong) {
	color: var(--text-color);
}

.chat-layout.light .dot {
	background: var(--grey-text);
}

.chat-layout.light .input-wrapper {
	background: linear-gradient(transparent, var(--background-color-main) 20%);
}

.chat-layout.light .input-container {
	background: var(--background-color);
	border-color: var(--border-color);
}

.chat-layout.light .chat-input {
	color: var(--text-color);
}

.chat-layout.light .chat-input::placeholder {
	color: var(--grey-text);
}

.chat-layout.light .send-button {
	background: var(--border-color);
	color: var(--grey-text);
}

.chat-layout.light .attach-button {
	color: var(--grey-text);
}

.chat-layout.light .attach-button:hover {
	color: var(--text-color);
}

/* Responsive: Tablet */
@media (min-width: 480px) {
	.welcome-greeting { font-size: 36px; }
}

@media (min-width: 600px) {
	.messagesCanvas { padding: 80px 24px 120px; }
	.message { max-width: 75%; }
}

@media (min-width: 768px) {
	.welcome-greeting { font-size: 48px; }
	.input-wrapper { padding-bottom: 40px; }
}

/* Responsive: Desktop */
@media (min-width: 1024px) {
	.input-wrapper { left: var(--sidebar-width); }
}
</style>
